import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { observerApi } from "../services/observerApi";
import { observerAuth } from "../services/firebaseAuth";
import { identifyVerifiedPractitioner } from "../services/analytics";
import { requestPractitionerPushAfterClaimVerification } from "../services/mobilePush";
export default function ObserverClaimPage() {
  const [p] = useSearchParams(),
    entity = p.get("entity") || "";
  const [method, setMethod] = useState("social_code"),
    [target, setTarget] = useState(""),
    [account, setAccount] = useState(""),
    [result, setResult] = useState<any>(),
    [code, setCode] = useState(""),
    [verified, setVerified] = useState(false),
    [invite, setInvite] = useState<any>(),
    [clientEmails, setClientEmails] = useState(""),
    [consent, setConsent] = useState(false),
    [deliveryStatus, setDeliveryStatus] = useState(""),
    [referral, setReferral] = useState<any>(),
    [error, setError] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      setResult(await observerApi.claim(entity, { method, target, account }));
      observerApi.event("claim_started", undefined, { source: method });
    } catch (x: any) {
      setError(x?.response?.data?.error || "Claim could not be started.");
    }
  };
  if (verified)
    return (
      <div className="ot-page">
        <div className="ot-empty">
          <h1>Identity verified</h1>
          <p>
            Claimed and identity-verified. Verification confirms this person
            controls the linked channels. It is not an endorsement or an
            assessment of quality.
          </p>
          <h2>Invite your clients to review</h2>
          <p>Invite every client with the same neutral copy. Never select by expected sentiment. Reviews remain email-verified, evidence-classed, privately evidenced where applicable, and human-moderated.</p>
          {invite ? <><label>Share link<input readOnly value={`${window.location.origin}/write-review?entity=${encodeURIComponent(entity)}&invite=${encodeURIComponent(invite.token)}`} /></label><p>{invite.standardCopy}</p><label>Consented client email list, one per line<textarea value={clientEmails} onChange={(e) => setClientEmails(e.target.value)} /></label><label><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} /> I confirm these clients consented to receive this neutral review invitation and I did not select them by expected sentiment.</label><button disabled={!consent || !clientEmails.trim()} onClick={async () => { const result = await observerApi.deliverReviewInvitation(invite.id, account, clientEmails.split(/[\n,]/).map((email) => email.trim()).filter(Boolean), consent); setDeliveryStatus(`${result.sent} neutral invitations sent.`); }}>{deliveryStatus || "Send neutral invitations"}</button></> : <button className="button button-ink" onClick={async () => setInvite(await observerApi.reviewInvitation(entity, account))}>Create neutral invitation link</button>}
          <h2>Invite peers to claim</h2>
          <p>Both sides bank one free Pro month only after the referred claim is identity-verified. Pro is dormant and trust-neutral: no rank, badges, review outcomes, verification, or moderation benefits.</p>
          {referral ? <label>Peer referral link<input readOnly value={referral.url} /></label> : <button className="button button-outline" onClick={async () => setReferral(await observerApi.referral(entity))}>Create peer referral link</button>}
        </div>
      </div>
    );
  return (
    <div className="ot-page">
      <header className="ot-page-head">
        <span className="section-kicker">
          For teachers, readers, coaches, and practitioners
        </span>
        <h1>Claim your public practice profile</h1>
        <p>
          Claiming is free and proves that you control an already-linked
          official website or channel used for your teachings, readings,
          content, community, or services. It is not an endorsement or quality
          assessment.
        </p>
      </header>
      {result ? (
        <form
          className="ot-form"
          onSubmit={async (e) => {
            e.preventDefault();
            setError("");
            try {
              const verification = await observerApi.verifyClaim(result.claimId, code);
              if (verification.subjectSessionToken) localStorage.setItem("observer_subject_session", verification.subjectSessionToken);
              const referralCode = localStorage.getItem("observer_referral_code");
              if (referralCode) { await observerApi.acceptReferral(referralCode, entity).catch(() => undefined); localStorage.removeItem("observer_referral_code"); }
              setVerified(true);
              try {
                const user = observerAuth().currentUser;
                if (user) {
                  identifyVerifiedPractitioner(user.uid);
                  await requestPractitionerPushAfterClaimVerification().catch(
                    () => undefined,
                  );
                }
              } catch {
                /* Push enrolment is optional and must never overturn a completed claim. */
              }
            } catch (x: any) {
              setError(x?.response?.data?.error || "Verification failed.");
            }
          }}
        >
          <p>{result.instructions}</p>
          <label>
            Verification code
            <input
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
          {error && <p className="ot-error">{error}</p>}
          <button>Verify control</button>
        </form>
      ) : (
        <form className="ot-form" onSubmit={submit}>
          <label>
            Your account email
            <input
              required
              type="email"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
            />
          </label>
          <label>
            Verification method
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="social_code">
                Code on an official public channel
              </option>
              <option value="domain_email">Email on the official domain</option>
            </select>
          </label>
          <label>
            {method === "social_code"
              ? "Already-linked official website or channel"
              : "Email address on the already-linked official domain"}
            <input
              required
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
          </label>
          <small>
            Claiming does not let anyone remove reviews, rewrite sourced
            history, or change ranking.
          </small>
          {error && <p className="ot-error">{error}</p>}
          <button>Start identity verification</button>
        </form>
      )}
    </div>
  );
}
